"use client";

// RepoMark — the repo mark: the brand cell with `</>` inside. It says "this
// project is code": beside a project's name (the SOLID form, about .85em, in
// --ink-3), in the repo view's head (28px, outline), in the activity panel's
// repos list and a parent's code grid (solid, 14px).
//
// John picked it on 2026-09-23: #29 "knockout code" is the solid mark, #26
// "brackets" the outline mark. Both are drawn by the git family
// (./GitGlyph, kind "repo"), so the mark and every other git glyph share one
// grid and one weight. The other drafts stay below, reachable by number.
//
// The geometry is ported point for point from the 45 drafts (the repo-mark
// generator, 2026-09-23): each glyph lives in a 64×64 box, and the cell is
// CELL.path (1071×831) scaled to 58 wide and centred, exactly as the drafts
// place it. `currentColor` everywhere, so the mark rides the ink ladder.
//
// REPO_MARK_DEFAULT is the one switch: change it and every mark in the product
// changes with it.
//
// Two forms of one idea:
//   · outline — an outlined cell with the branch drawn inside it (#1, #27, #43)
//   · knockout — a solid cell with the branch cut out of it (#6, #9, #41)
// `solid` asks for the knockout form. A knockout variant already is one; an
// outline variant is re-cut as its own knockout (its glyph, a touch heavier so
// it survives at 14px); the free-standing cells (#16) are already solid.

import { useId } from "react";
import { CELL } from "../../lib/brand/cell";
import GitGlyph from "./GitGlyph";

/** John's pick (2026-09-23): #29 knockout code, whose outline form is #26 brackets. */
export const REPO_MARK_DEFAULT = 29;
/** the pair the default draws: `solid` → #29, outline → #26 (GitGlyph "repo"). */
export const REPO_MARK_PAIR = { solid: 29, outline: 26 } as const;

type Part =
  | { t: "path"; d: string; w: number }
  | { t: "dot"; x: number; y: number; r: number }
  | { t: "cell"; cx: number; cy: number; w: number };

type Variant =
  | { form: "outline"; rim: number; glyph: Part[] }
  | { form: "knock"; glyph: Part[] }
  | { form: "free"; parts: Part[] };

const p = (d: string, w: number): Part => ({ t: "path", d, w });
const dot = (x: number, y: number, r = 3.4): Part => ({ t: "dot", x, y, r });
const cell = (cx: number, cy: number, w: number): Part => ({ t: "cell", cx, cy, w });

/** the drafted variants, by their number on the sheet. */
export const REPO_MARKS: Record<number, { name: string; v: Variant }> = {
  26: {
    name: "brackets",
    v: { form: "outline", rim: 2.6, glyph: [p("M24 24 L16 32 L24 40 M40 24 L48 32 L40 40 M35 22 L29 42", 2.8)] },
  },
  29: {
    name: "knockout code",
    v: { form: "knock", glyph: [p("M24 24 L16 32 L24 40 M40 24 L48 32 L40 40 M35 21 L29 43", 3.6)] },
  },
  1: {
    name: "fork up",
    v: { form: "outline", rim: 2.6, glyph: [p("M32 45 V20 M32 37 C32 31 40 30 40 24", 3), dot(32, 45), dot(32, 20), dot(40, 23)] },
  },
  6: {
    name: "knockout fork",
    v: {
      form: "knock",
      glyph: [p("M32 46 V19 M32 37 C32 31 41 30 41 24", 3.6), dot(32, 45, 4.2), dot(32, 20, 4.2), dot(41, 23, 4.2)],
    },
  },
  9: {
    name: "knockout Y",
    v: { form: "knock", glyph: [p("M32 46 V32 C32 26 24 26 24 20 M32 32 C32 26 40 26 40 20", 3.8)] },
  },
  16: {
    name: "two to one",
    v: {
      form: "free",
      parts: [p("M20 42 C20 34 32 33 32 24 M44 42 C44 34 32 33 32 24", 2.6), cell(20, 44, 14), cell(44, 44, 14), cell(32, 21, 14)],
    },
  },
  27: {
    name: "bracket fork",
    v: {
      form: "outline",
      rim: 2.6,
      glyph: [
        p("M23 24 L15 32 L23 40", 2.8),
        p("M30 40 V24 M30 34 C30 29 38 29 38 24", 2.8),
        dot(30, 41, 3),
        dot(30, 23, 3),
        dot(38, 23, 3),
      ],
    },
  },
  41: {
    name: "bold Y",
    v: { form: "knock", glyph: [p("M32 46 V33 L22 20 M32 33 L42 20", 5.2)] },
  },
  43: {
    name: "thick fork",
    v: { form: "outline", rim: 3.4, glyph: [p("M32 44 V21 M32 36 C32 30 41 30 41 24", 4.4)] },
  },
};

/** the cell at (cx, cy), `w` wide in the 64 box — the drafts' `cell()` helper. */
function cellTransform(cx: number, cy: number, w: number): { transform: string; s: number } {
  const s = w / CELL.width;
  const h = CELL.height * s;
  return { transform: `translate(${(cx - w / 2).toFixed(2)} ${(cy - h / 2).toFixed(2)}) scale(${s.toFixed(5)})`, s };
}

function CellShape({ cx, cy, w, fill, stroke }: { cx: number; cy: number; w: number; fill: string; stroke?: number }) {
  const { transform, s } = cellTransform(cx, cy, w);
  return (
    <path
      d={CELL.path}
      transform={transform}
      fill={fill}
      stroke={stroke ? "currentColor" : undefined}
      strokeWidth={stroke ? stroke / s : undefined}
    />
  );
}

/** a glyph part in one ink (black in a mask, currentColor on the page). */
function Glyph({ parts, ink, boost = 1 }: { parts: Part[]; ink: string; boost?: number }) {
  return (
    <>
      {parts.map((g, i) =>
        g.t === "path" ? (
          <path key={i} d={g.d} fill="none" stroke={ink} strokeWidth={g.w * boost} strokeLinecap="round" strokeLinejoin="round" />
        ) : g.t === "dot" ? (
          <circle key={i} cx={g.x} cy={g.y} r={g.r * boost} fill={ink} />
        ) : (
          <CellShape key={i} cx={g.cx} cy={g.cy} w={g.w} fill={ink} />
        ),
      )}
    </>
  );
}

export default function RepoMark({
  variant = REPO_MARK_DEFAULT,
  size = 16,
  solid = false,
  title,
  className,
}: {
  variant?: number;
  /** px, or any CSS length (".85em" beside a name). */
  size?: number | string;
  /** the knockout form (for name marks). */
  solid?: boolean;
  /** a label for assistive tech; omitted, the mark is decorative. */
  title?: string;
  className?: string;
}) {
  // a mask id per instance, made safe for url(#…)
  const raw = useId();
  // the chosen pair is the git family's repo glyph, one geometry for both forms
  if (variant === REPO_MARK_PAIR.solid || variant === REPO_MARK_PAIR.outline || !REPO_MARKS[variant]) {
    return <GitGlyph kind="repo" solid={solid} size={size} title={title} className={className} />;
  }
  const maskId = `rpm${raw.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const entry = REPO_MARKS[variant] ?? REPO_MARKS[REPO_MARK_DEFAULT];
  const v = entry.v;

  // the knockout: a solid cell with the glyph cut out through a mask
  const knock = (glyph: Part[], boost = 1) => (
    <>
      <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="64">
        <rect width="64" height="64" fill="black" />
        <CellShape cx={32} cy={32} w={58} fill="white" />
        <Glyph parts={glyph} ink="black" boost={boost} />
      </mask>
      <rect width="64" height="64" fill="currentColor" mask={`url(#${maskId})`} />
    </>
  );

  let body: React.ReactNode;
  if (v.form === "knock") body = knock(v.glyph);
  else if (v.form === "outline")
    body = solid ? (
      knock(v.glyph, 1.2)
    ) : (
      <>
        <CellShape cx={32} cy={32} w={58} fill="none" stroke={v.rim} />
        <Glyph parts={v.glyph} ink="currentColor" />
      </>
    );
  else body = <Glyph parts={v.parts} ink="currentColor" />;

  return (
    <svg
      className={className}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      focusable="false"
      style={{ flex: "0 0 auto", overflow: "visible" }}
    >
      {body}
    </svg>
  );
}
